import { cookies, setCookie, removeCookie } from 'sharedKernel/cookies/behavior'
import {
  getCredLocalStorage,
  setCredLocalStorage,
  removeCredLocalStorage,
  ICredLocalStorage
} from './credLocalStorage'
import { LOGOUT, SET_AUTH, SET_PERMISSIONS } from './authActions'

// The raw permissions payload (UUID `id` + `action` on every feature/access)
// URL-encodes to ~10KB and exceeds the browser's ~4KB per-cookie limit, so the
// `_perm` cookie is silently dropped and the side menu collapses to only
// "Configurações" on a fresh boot. Strip the ids/actions (keeping the
// `code`/`accesses[].code` the menu and hasAccess() need) so the cookie persists
// and survives a shell -> legacy redirect. Mirrors libs/shared/src/auth/authReducer.ts.
const PERM_COOKIE_DROP_KEYS = new Set(['id', 'action'])
const trimPermissionsForCookie = (payload: unknown): unknown =>
  JSON.parse(JSON.stringify(payload, (key, value) => (PERM_COOKIE_DROP_KEYS.has(key) ? undefined : value)))

// Tiny, always-fits companion cookie holding just the feature codes. `_perm` can
// still exceed the ~4KB per-cookie limit for big users and be dropped; the side
// menu only needs the codes, so persist them separately so the menu survives a
// shell -> legacy redirect regardless of `_perm`'s size. Mirrors libs/shared.
const extractFeatureCodesForCookie = (payload: any): string[] =>
  Array.isArray(payload?.features)
    ? payload.features.map((f: any) => String(f?.code)).filter((c: string) => c && c !== 'undefined')
    : []

const AUTH_DEFAULT_USER_VALUE = JSON.parse('{}')
const AUTH_DEFAULT_IS_AUTHENTICATED_VALUE = undefined
const AUTH_DEFAULT_PERMISSIONS_VALUE = undefined

const credCookieData = cookies.get('_cred')?.data
const credLocalStorageData = getCredLocalStorage()

export const AUTH_INITIAL_STATE: IAuthState = {
  user: credCookieData ? { ...credCookieData, ...credLocalStorageData } : AUTH_DEFAULT_USER_VALUE,
  isAuthenticated: cookies.get('_cred') ?? AUTH_DEFAULT_IS_AUTHENTICATED_VALUE,
  permissions: cookies.get('_perm') ?? AUTH_DEFAULT_PERMISSIONS_VALUE
}

interface IAuthUser {
  id: string
  username: string
  role: string
  token: string
}

export interface IAuthAccess {
  id: string
  code: string
  action?: string
}

export interface IAuthFeature {
  id: string
  code: string
  access: IAuthAccess[]
}

export interface IAuthPermission {
  id: string
  name: string
  features: IAuthFeature[]
}

export interface IAuthAction {
  type: string
  payload: Partial<IAuthState>
}

export interface IAuthState {
  user?: IAuthUser
  permissions?: IAuthPermission
  isAuthenticated: boolean
}

export const AuthReducer = (state: IAuthState = AUTH_INITIAL_STATE, action: IAuthAction): any => {
  switch (action.type) {
    case SET_AUTH:
      const cookieData = {
        id: (action.payload as any)?.id,
        data: {
          id: (action.payload as any)?.data?.id || (action.payload as any)?.id,
          email: (action.payload as any)?.data?.email,
          name: (action.payload as any)?.data?.name,
          token: (action.payload as any)?.data?.token,
          role: (action.payload as any)?.data?.role
        }
      }

      const localStorageData: ICredLocalStorage = {
        id: cookieData.data.id,
        temporaryToken: (action.payload as any)?.data?.temporaryToken,
        isSuperAdmin: (action.payload as any)?.data?.isSuperAdmin ?? false,
        company: (action.payload as any)?.data?.company ? {
          id: (action.payload as any)?.data?.company?.id,
          name: (action.payload as any)?.data?.company?.name,
          type: (action.payload as any)?.data?.company?.type,
          cityHallInfo: (action.payload as any)?.data?.company?.cityHallInfo
        } : undefined,
        profiles: (action.payload as any)?.data?.profiles ?
          (action.payload as any)?.data?.profiles.map((p: any) => ({
            id: p.id,
            role: { id: p.role?.id, name: p.role?.name },
            company: {
              id: p.company?.id,
              name: p.company?.name,
              contactName: p.company?.contactName,
              type: p.company?.type
            }
          })) : undefined,
        updatedAt: (action.payload as any)?.data?.updatedAt,
        powerBiUrl: (action.payload as any)?.data?.powerBiUrl,
        startupInfo: (action.payload as any)?.data?.startupInfo ? {
          isSimplifiedResolver: (action.payload as any)?.data?.startupInfo?.isSimplifiedResolver
        } : undefined,
        cityHallInfo: (action.payload as any)?.data?.cityHallInfo ? {
            Id: (action.payload as any)?.data?.cityHallInfo?.id,
            Name: (action.payload as any)?.data?.cityHallInfo?.name,
            MayorName: (action.payload as any)?.data?.cityHallInfo?.mayorName,
            TeamSize: (action.payload as any)?.data?.cityHallInfo?.teamSize,
            HasSecretary: (action.payload as any)?.data?.cityHallInfo?.hasSecretary,
            Secretary: (action.payload as any)?.data?.cityHallInfo?.secretary,
            SecretaryResponsible: (action.payload as any)?.data?.cityHallInfo?.secretaryResponsible
          }
        : undefined
      }

      setCookie('_cred', cookieData)
      setCredLocalStorage(localStorageData)

      return {
        ...state,
        user: action.payload,
        isAuthenticated: true
      }

    case SET_PERMISSIONS:
      setCookie('_perm', trimPermissionsForCookie(action.payload))
      setCookie('_permCodes', extractFeatureCodesForCookie(action.payload))
      return {
        ...state,
        permissions: action.payload
      }

    case LOGOUT:
      removeCookie('_cred')
      removeCookie('_perm')
      removeCookie('_permCodes')
      removeCookie('_usrData')
      removeCookie('_profImg')
      removeCookie('_behaveAsCompany')
      removeCredLocalStorage()
      return {
        user: AUTH_DEFAULT_USER_VALUE,
        isAuthenticated: AUTH_DEFAULT_IS_AUTHENTICATED_VALUE,
        permissions: AUTH_DEFAULT_PERMISSIONS_VALUE
      }
    default:
      return state
  }
}
